import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { OFFICES, FIRM_DISCIPLINES, FIRM_PHONE, MAIN_OFFICE_PHONE, TEXAS_METROS } from "@/lib/content";
import SEO from "@/components/SEO";
import { SITE } from "@/lib/seo";
import { firmOrganizationSchema, breadcrumbSchema } from "@/lib/schemas";

export default function Locations() {
  return (
    <div className="bg-ivory" data-testid="locations-page">
      <SEO
        title="Texas Offices — Rooted in Texas · Serving the Entire Lone Star State"
        description="Principal offices in Frisco with counsel available across Dallas–Fort Worth, Austin, San Antonio, the Houston metroplex, Tyler, Midland/Odessa, and El Paso."
        path="/locations"
        schema={[
          firmOrganizationSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Locations", path: "/locations" },
          ]),
        ]}
      />

      {/* ================================================================
          HERO — cinematic banner using the Texas landscape hero image.
      ================================================================= */}
      <section className="relative bg-mahogany overflow-hidden" data-testid="locations-hero">
        <div className="relative h-[520px] md:h-[600px]">
          <img
            src="/hero-lonestar-0.jpg"
            alt="Texas hill country at golden hour with Dallas skyline"
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(30,58,95,0.78) 0%, rgba(30,58,95,0.55) 35%, rgba(30,58,95,0.12) 65%, rgba(30,58,95,0) 100%)",
            }}
          />
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-navy/80 to-transparent pointer-events-none" />

          <div className="relative h-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 flex items-center">
            <div className="max-w-2xl pt-24">
              <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
                <span className="h-px w-10 bg-brass/70" />
                Rooted in Texas
              </div>
              <h1
                className="mt-6 font-display text-ivory leading-[1.02] text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem]"
                style={{ letterSpacing: "0.005em", fontVariant: "small-caps" }}
              >
                Rooted in Texas.<br />
                <span className="italic font-cormorant text-brass" style={{ fontVariant: "normal" }}>
                  Serving the entire Lone Star State.
                </span>
              </h1>
              <div className="mt-8 flex items-center gap-4">
                <span className="h-px w-16 bg-brass/70" />
                <Star size={11} className="text-brass fill-brass" />
                <span className="h-px w-16 bg-brass/70" />
              </div>
              <p
                className="mt-8 max-w-xl text-lg text-ivory/85 font-light leading-relaxed"
                style={{ textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}
              >
                Principal offices in Frisco with counsel available across Dallas–Fort Worth,
                Austin, San Antonio, the Houston metroplex, Tyler, Midland/Odessa, and El Paso.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          PRINCIPAL CONTACT — tucked between hero and gallery.
      ================================================================= */}
      <section className="bg-linen py-14 md:py-16" data-testid="locations-principal-strip">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          <div className="hairline pt-5">
            <div className="text-[10px] uppercase tracking-[0.32em] text-slate2/60">
              Principal Office &nbsp;·&nbsp; Frisco
            </div>
            <a
              href={MAIN_OFFICE_PHONE.href}
              className="mt-1 block font-display text-3xl md:text-4xl text-navy hover:text-brass transition"
              data-testid="locations-phone-main-office"
            >
              {MAIN_OFFICE_PHONE.display}
            </a>
            <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-slate2/70">
              2591 Dallas Parkway, Suite 300 &nbsp;·&nbsp; Frisco, Texas 75034
            </div>
          </div>
          <div className="hairline pt-5">
            <div className="text-[10px] uppercase tracking-[0.32em] text-slate2/60">
              Toll-Free &nbsp;·&nbsp; Statewide
            </div>
            <a
              href={FIRM_PHONE.href}
              className="mt-1 block font-display text-3xl md:text-4xl text-navy hover:text-brass transition"
              data-testid="locations-phone"
            >
              {FIRM_PHONE.display}
            </a>
            <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-slate2/70">
              {FIRM_DISCIPLINES}
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          OFFICES GALLERY — five regional office cards with AI-generated
          location banners. Editorial 2-column grid.
      ================================================================= */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-4 text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
            <span className="h-px w-10 bg-brass/60" />
            Five Regional Offices
            <span className="h-px w-10 bg-brass/60" />
          </div>
          <h2 className="mt-6 font-display text-navy text-4xl md:text-5xl leading-[1.05]">
            Where we serve.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-20">
          {OFFICES.map((o, idx) => (
            <Link
              key={o.slug}
              to={`/locations/${o.slug}`}
              className="group block"
              data-testid={`locations-card-${o.slug}`}
            >
              <div className="aspect-[5/3] overflow-hidden bg-navy relative">
                <img
                  src={o.hero}
                  alt={o.heroAlt}
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/75 via-navy/10 to-transparent" />
                <div className="absolute bottom-5 left-6 right-6 text-ivory">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-brass font-medium">
                    {String(idx + 1).padStart(2, "0")} &nbsp;·&nbsp; {o.kicker}
                  </div>
                  <div className="mt-1 font-display text-2xl md:text-3xl text-ivory">
                    {o.region}
                  </div>
                </div>
                {/* By Appointment Only — top-right badge on the image */}
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center gap-2 bg-navy/85 text-ivory px-3.5 py-1.5 text-[9px] uppercase tracking-[0.28em] font-semibold backdrop-blur-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-brass" />
                    By Appointment Only
                  </span>
                </div>
              </div>
              <div className="mt-6 pt-6 flex items-start justify-between gap-6">
                <div>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    {o.label === "Principal Offices" && (
                      <span className="text-[10px] tracking-[0.28em] uppercase text-brass font-sans font-semibold">
                        Principal Office
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-xs uppercase tracking-[0.22em] text-slate2/60">
                    {o.serves}
                  </div>
                  <div className="mt-4 text-slate2 font-light leading-relaxed">{o.line1}</div>
                  <div className="text-slate2 font-light">{o.line2}</div>
                  <div className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.26em] text-brass font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-brass" />
                    By Appointment Only
                  </div>
                </div>
                <ArrowRight
                  size={28}
                  className="text-brass group-hover:translate-x-2 transition-transform shrink-0 mt-1"
                />
              </div>
              <p className="mt-5 font-cormorant italic text-lg text-slate2 max-w-md leading-snug">
                {o.headline}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* ================================================================
          STATEWIDE COVERAGE — extended-reach cities without physical offices.
      ================================================================= */}
      <section className="panel-navy py-24 md:py-32" data-testid="locations-statewide">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="kicker kicker-ivory">Statewide Coverage</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl text-ivory leading-[1.02]">
                Where our physical<br/>
                <span className="italic font-cormorant text-brass">offices do not reach.</span>
              </h2>
              <p className="mt-8 font-cormorant italic text-xl text-ivory/85 leading-snug max-w-md">
                Counsel available across the entire Lone Star State — from East Texas
                to the Permian Basin to the western border.
              </p>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-ivory/85 font-light">
              <p className="text-base md:text-lg leading-relaxed">
                Beyond our five physical offices, the firm regularly serves clients across Texas
                by scheduled travel, virtual conference, and in coordination with local counsel
                where required. Same-week response for time-sensitive matters. Standing engagements
                for enterprises with statewide operations.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 pt-6 hairline-top" data-testid="statewide-cities">
                {[
                  "Dallas–Fort Worth",
                  "Austin",
                  "San Antonio",
                  "Houston Metroplex",
                  "Tyler · East Texas",
                  "Midland / Odessa · Permian",
                  "El Paso · Far West Texas",
                  "By appointment — all counties",
                ].map((city, i) => (
                  <li
                    key={city}
                    className="flex items-baseline gap-3 font-cormorant italic text-lg text-brass leading-snug"
                    data-testid={`statewide-city-${i}`}
                  >
                    <Star size={9} className="text-brass fill-brass shrink-0 translate-y-1" />
                    <span className="text-ivory">{city}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          TOP TEXAS METROS — editorial 50-word paragraphs per major metro.
          Deep SEO lift: crawlable prose paired with JSON-LD areaServed.
      ================================================================= */}
      <section className="bg-ivory grain py-24 md:py-32" data-testid="locations-metros">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-14 md:mb-20">
            <div className="kicker">The Ten Metros</div>
            <h2 className="mt-6 font-display text-navy text-4xl md:text-5xl leading-[1.02]">
              Counsel across every major Texas market —
              <span className="italic font-cormorant text-oxblood"> and the towns in between.</span>
            </h2>
            <p className="mt-6 font-cormorant italic text-lg text-slate2 leading-snug max-w-2xl">
              Where we most frequently represent Texas families and business owners, and
              what the work typically looks like in each region.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-14" data-testid="metro-paragraphs">
            {TEXAS_METROS.map((m, i) => (
              <article
                key={m.slug}
                className="hairline-top pt-8"
                data-testid={`metro-${m.slug}`}
              >
                <div className="flex items-baseline gap-4">
                  <span className="text-[10px] tracking-[0.32em] uppercase text-brass font-medium">
                    No. {String(i + 1).padStart(2, "0")}
                  </span>
                  <Star size={9} className="text-brass fill-brass" />
                </div>
                <h3 className="mt-3 font-display text-2xl md:text-3xl text-navy leading-[1.1]">
                  {m.name}
                </h3>
                <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-slate2/60">
                  {m.region}
                </div>
                <p className="mt-5 text-slate2 leading-relaxed text-[15px]">
                  {m.paragraph}
                </p>
                <div className="mt-5 text-xs font-cormorant italic text-brass">
                  Typical engagements: <span className="text-slate2 not-italic uppercase tracking-[0.22em] text-[10px]">{m.typical}</span>
                </div>
              </article>
            ))}
          </div>

          <p className="mt-16 max-w-3xl font-cormorant italic text-slate2 text-lg leading-snug">
            Beyond these ten metros, the firm regularly represents clients in Waco, Lubbock,
            Amarillo, Beaumont, Longview, Wichita Falls, Abilene, Bryan–College Station,
            Laredo, Brownsville, McAllen, Corpus Christi, and every county courthouse in
            between. If your matter is in Texas, we are within reach.
          </p>
        </div>
      </section>

      {/* ================================================================
          CLOSING CTA — light modern panel to funnel to intake.
      ================================================================= */}
      <section className="bg-linen py-20 md:py-24" data-testid="locations-closer">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-3">
            <span className="h-px w-10 bg-brass/60" />
            <Star size={11} className="text-brass fill-brass" />
            <span className="h-px w-10 bg-brass/60" />
          </div>
          <h2 className="mt-6 font-display text-navy text-3xl md:text-4xl leading-tight">
            Wherever you are in Texas, experienced legal counsel is within reach.
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-slate2 font-cormorant italic text-lg md:text-xl">
            Meet with us by phone or secure video conference—your first 30 minutes of counsel are on the firm.
          </p>
          <div className="mt-10">
            <Link to="/intake" className="btn-gold" data-testid="locations-cta-intake">
              Request a Conference
            </Link>
          </div>
        </div>
      </section>

      {/* Areas We Serve — SEO index of statewide coverage */}
      <section className="bg-ivory hairline-top py-20 md:py-24" data-testid="areas-we-serve">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-3">
            <span className="h-px w-14 bg-brass/50" />
            <Star size={9} className="text-brass fill-brass" />
            <span className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
              Areas We Serve
            </span>
          </div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl text-navy leading-[1.05] max-w-3xl">
            Serving clients across the entire{" "}
            <span className="italic font-cormorant text-oxblood">State of Texas.</span>
          </h2>
          <p className="mt-4 max-w-2xl text-slate2 font-cormorant italic text-lg">
            From the DFW Metroplex to the Rio Grande Valley, from the Permian Basin to the Piney Woods —
            counsel available in every major Texas market and its suburbs.
          </p>

          <ul
            className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-2 text-xs text-slate2 font-light"
            data-testid="areas-we-serve-list"
          >
            {SITE.areasServed
              .filter((c) => c !== "Texas" && c !== "State of Texas")
              .map((city) => (
                <li key={city} className="truncate">{city}</li>
              ))}
          </ul>

          <p className="mt-10 text-[11px] uppercase tracking-[0.3em] text-slate2/70 max-w-3xl">
            Not seeing your city? Every county in Texas is within our reach.{" "}
            <Link to="/contact" className="text-brass hover:text-navy underline underline-offset-4">
              Write to the firm
            </Link>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
